/**
 * Backend bridge for Webba ID OAuth steps.
 *
 * The backend exchanges the Webba code (with our appSecret) and mints a
 * short-lived Stark session JWT (HS256). The PWA holds the JWT + the Webba
 * refresh token; /api/* uses the JWT, refresh-when-expiring uses the Webba
 * refresh token via /webba/refresh.
 *
 * See `apps/stark-server/src/auth.ts` (mint/verify) and
 * `apps/stark-server/src/http.ts` (`/webba/{callback,refresh}`).
 */

const BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '')

export interface StarkSessionUser {
  id: string
  email: string
  name: string
  avatar?: string
}

export interface StarkSession {
  /** HS256 JWT signed by Stark; sent as Bearer to /api/*. */
  sessionJwt: string
  /** Absolute UNIX ms when sessionJwt expires. Refresh well before. */
  sessionExpiresAt: number
  /** Long-lived Webba refresh token used to mint a new sessionJwt. */
  webbaRefreshToken: string
  user: StarkSessionUser
}

interface RawErrorResponse {
  error: string
  reason?: string
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    let detail = ''
    try {
      const j = (await res.json()) as RawErrorResponse
      detail = j.reason ? `${j.error}: ${j.reason}` : j.error
    } catch {
      detail = `HTTP ${res.status}`
    }
    throw new Error(detail)
  }
  return (await res.json()) as T
}

/** Exchange the OAuth code (with PKCE verifier) for a Stark session via our backend. */
export async function exchangeCode(code: string, codeVerifier: string): Promise<StarkSession> {
  return await postJson<StarkSession>('/webba/callback', { code, codeVerifier })
}

/** Trade a Webba refresh token for a fresh Stark session (new JWT + rotated refresh token). */
export async function refreshSession(refreshToken: string): Promise<StarkSession> {
  return await postJson<StarkSession>('/webba/refresh', { refreshToken })
}

const PKCE_PREFIXES = ['stark', 'webba'] as const

/**
 * Look up the PKCE verifier the SDK stored before redirect.
 * The SDK uses `${storagePrefix}_pkce_${state}`; we configure storagePrefix='stark'
 * but fall back to 'webba' in case the prefix changes or storage was migrated.
 */
export function readPkceVerifier(state: string): string | null {
  for (const prefix of PKCE_PREFIXES) {
    const raw = localStorage.getItem(`${prefix}_pkce_${state}`)
    if (!raw) continue
    try {
      const parsed = JSON.parse(raw) as { codeVerifier?: string }
      if (parsed.codeVerifier) return parsed.codeVerifier
    } catch {
      // malformed entry, try next prefix
    }
  }
  // Cookie fallback (the SDK also writes a backup cookie 'webba_pkce').
  const cookieMatch = document.cookie.match(/(?:^|;\s*)webba_pkce=([^;]+)/)
  if (cookieMatch) {
    try {
      const parsed = JSON.parse(decodeURIComponent(cookieMatch[1] ?? '')) as {
        state?: string
        codeVerifier?: string
      }
      if (parsed.state === state && parsed.codeVerifier) return parsed.codeVerifier
    } catch {
      // ignore
    }
  }
  return null
}

/** Clean up the PKCE storage (both prefixes + cookie) after a successful exchange. */
export function clearPkce(state: string): void {
  for (const prefix of PKCE_PREFIXES) {
    localStorage.removeItem(`${prefix}_pkce_${state}`)
  }
  document.cookie = 'webba_pkce=; Max-Age=0; Path=/'
}
